import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { QueueEntryPoint } from '../../lib/agentPrompts';
import { PROMPT_DEFAULTS, checkTemplate, fetchPromptVersions, savePrompt, type PromptId, type PromptVersion } from '../../lib/prompts';
import { PROMPT_VAR } from '../../lib/promptTemplates.mjs';
import { supabase } from '../../lib/supabase';
import { useUser } from '../../context/UserContext';
import { useUrlParam } from '../../hooks/useUrlParam';
import { Badge, Button, Card, Input, useToast } from '../ui';
import { promptPre, when } from './promptStyles';

const faint: React.CSSProperties = { color: 'var(--text-faint)' };
/** A template with its {{variables}} picked out, so the fixed prose and the live data read apart. */
export function TemplateText({ text }: { text: string }) {
  const parts: React.ReactNode[] = [];
  let last = 0;
  for (const m of text.matchAll(PROMPT_VAR)) {
    const at = m.index ?? 0;
    if (at > last) parts.push(text.slice(last, at));
    parts.push(
      <span key={at} style={{ color: 'var(--accent-text)', background: 'var(--accent-soft)', borderRadius: '3px' }}>
        {m[0]}
      </span>,
    );
    last = at + m[0].length;
  }
  parts.push(text.slice(last));
  return <pre style={promptPre}>{parts}</pre>;
}

const draftKey = (id: string) => `cockpit.promptDraft.${id}`;
const readDraft = (id: string) => {
  try {
    return sessionStorage.getItem(draftKey(id));
  } catch {
    return null;
  }
};
const writeDraft = (id: string, text: string | null) => {
  try {
    if (text == null) sessionStorage.removeItem(draftKey(id));
    else sessionStorage.setItem(draftKey(id), text);
  } catch {
    /* private mode: the draft just doesn't survive a refresh */
  }
};

function useUserNames(ids: (string | null)[]) {
  const [names, setNames] = useState<Map<string, string>>(new Map());
  const key = [...new Set(ids.filter(Boolean))].sort().join(',');
  useEffect(() => {
    if (!key) return;
    let cancelled = false;
    void supabase
      .from('users')
      .select('id, name')
      .in('id', key.split(','))
      .then(({ data }) => {
        if (!cancelled) setNames(new Map((data ?? []).map((u) => [u.id as string, (u.name as string) ?? 'Someone'])));
      });
    return () => {
      cancelled = true;
    };
  }, [key]);
  return names;
}

export interface PromptState {
  /** What the button renders right now. */
  current: string;
  /** The newest save, or null while it's the built-in default. */
  saved: PromptVersion | null;
  /** The text being edited, or null when not editing. */
  draft: string | null;
}

/**
 * The editable template behind one queue button (Agents → Prompts → a button). Saving inserts a row in
 * agent_prompts; the button renders from the newest row from then on. `?edit=1` keeps the editor open through a
 * refresh (the draft rides in sessionStorage) and `?version=<id>` opens one save from the history.
 */
export default function PromptEditor({
  entry,
  onState,
  children,
}: {
  entry: QueueEntryPoint & { prompt: NonNullable<QueueEntryPoint['prompt']> };
  onState: (s: PromptState) => void;
  /** Rendered between the template and its history: the preview, so it sits right under the text being edited. */
  children?: React.ReactNode;
}) {
  const id = entry.prompt.id as PromptId;
  const fallback = PROMPT_DEFAULTS[id];
  const { user } = useUser();
  const toast = useToast();
  const [versions, setVersions] = useState<PromptVersion[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [editParam, setEditParam] = useUrlParam('edit');
  const [versionParam, setVersionParam] = useUrlParam('version');
  const [, setParams] = useSearchParams();
  const [draft, setDraft] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const area = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchPromptVersions(id)
      .then((v) => !cancelled && setVersions(v))
      .catch((err: Error) => !cancelled && setLoadError(err.message));
    return () => {
      cancelled = true;
    };
  }, [id]);

  const saved = versions?.[0] ?? null;
  const current = saved?.template ?? fallback;
  const editing = editParam === '1' && versions != null;

  // Open the editor from the URL (refresh, back/forward) with the draft that was being typed, if any.
  useEffect(() => {
    if (!versions) return;
    if (editParam === '1' && draft == null) setDraft(readDraft(id) ?? current);
    if (editParam !== '1' && draft != null) setDraft(null);
  }, [editParam, versions, draft, id, current]);

  useEffect(() => {
    if (draft != null) writeDraft(id, draft);
  }, [draft, id]);

  useEffect(() => {
    onState({ current, saved, draft: editing ? draft : null });
  }, [current, saved, draft, editing, onState]);

  const names = useUserNames((versions ?? []).map((v) => v.created_by));
  const known = useMemo(() => Object.keys(entry.prompt.vars), [entry.prompt.vars]);
  const check = useMemo(() => checkTemplate(draft ?? current, known), [draft, current, known]);
  const changed = draft != null && draft !== current;
  // The note is how the agent finds its skill: say so when an edit drops the line the default uses to name it.
  const skillName = entry.skill.split(' ')[0];
  const lostSkill = draft != null && fallback.includes(`${skillName} skill`) && !draft.includes(`${skillName} skill`);

  // One URL update for both: two setters in a row each start from the same old query string, so the second undoes the first.
  const closeAll = () =>
    setParams((prev) => {
      const p = new URLSearchParams(prev);
      p.delete('edit');
      p.delete('version');
      return p;
    });

  const stopEditing = () => {
    writeDraft(id, null);
    setDraft(null);
    setNote('');
    setEditParam(null);
  };

  const save = async (template: string | null, why: string) => {
    if (!user?.id) {
      toast.error('Sign in to save a prompt.');
      return;
    }
    setBusy(true);
    try {
      const row = await savePrompt(id, template, user.id, why);
      setVersions((v) => [row, ...(v ?? [])]);
      writeDraft(id, null);
      setDraft(null);
      setNote('');
      closeAll();
      toast.success(template == null ? 'Back to the built-in prompt. New jobs use it from now on.' : 'Saved. New jobs from this button use it from now on.');
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const insertVar = (name: string) => {
    const el = area.current;
    const text = draft ?? current;
    const at = el ? el.selectionStart : text.length;
    const end = el ? el.selectionEnd : text.length;
    const token = `{{${name}}}`;
    setDraft(text.slice(0, at) + token + text.slice(end));
    requestAnimationFrame(() => {
      if (!el) return;
      el.focus();
      el.setSelectionRange(at + token.length, at + token.length);
    });
  };

  const viewing = versionParam ? (versionParam === 'default' ? { id: 'default', template: null } : versions?.find((v) => v.id === versionParam) ?? null) : null;

  if (loadError) {
    return (
      <Card eyebrow="Prompt template" title="Couldn’t load the saved prompt">
        <span style={faint}>{loadError}</span>
      </Card>
    );
  }

  return (
    <>
      <Card
        eyebrow="Prompt template · what this button sends, with {{variables}} filled in from live data"
        title={saved?.template ? 'Your version' : 'Built-in default'}
        actions={
          versions == null ? (
            <span style={faint}>…</span>
          ) : editing ? (
            <div style={{ display: 'flex', gap: '8px' }}>
              <Button size="sm" variant="ghost" onClick={stopEditing} disabled={busy}>
                Cancel
              </Button>
              <Button size="sm" onClick={() => void save(draft ?? current, note)} disabled={busy || !changed || check.unknown.length > 0}>
                {busy ? 'Saving…' : 'Save prompt'}
              </Button>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              {saved?.template ? (
                <Badge tone="accent">
                  Edited {when(saved.created_at)}
                  {saved.created_by && names.get(saved.created_by) ? ` · ${names.get(saved.created_by)}` : ''}
                </Badge>
              ) : (
                <Badge tone="neutral">Default</Badge>
              )}
              <Button
                size="sm"
                variant="secondary"
                onClick={() =>
                  setParams((prev) => {
                    const p = new URLSearchParams(prev);
                    p.delete('version');
                    p.set('edit', '1');
                    return p;
                  })
                }
              >
                Edit
              </Button>
            </div>
          )
        }
      >
        {editing && draft != null ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
            <textarea
              ref={area}
              className="ck-textarea"
              value={draft}
              spellCheck={false}
              onChange={(e) => setDraft(e.target.value)}
              rows={Math.min(40, Math.max(10, draft.split('\n').length + 2))}
              style={{ ...promptPre, width: '100%', resize: 'vertical', boxSizing: 'border-box' }}
            />
            {check.unknown.length ? (
              <div style={{ color: 'var(--status-down)', fontSize: 'var(--text-sm)' }}>
                This button doesn’t fill {check.unknown.map((v) => `{{${v}}}`).join(', ')}. Fix or remove {check.unknown.length === 1 ? 'it' : 'them'} to save.
              </div>
            ) : null}
            {check.unused.length ? (
              <div style={{ ...faint, fontSize: 'var(--text-xs)' }}>
                Not used, so the agent won’t see it: {check.unused.map((v) => `{{${v}}}`).join(', ')}.
              </div>
            ) : null}
            <Input fieldSize="sm" placeholder="What changed and why (optional, shows in the history)" value={note} onChange={(e) => setNote(e.target.value)} />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              <span style={{ ...faint, fontSize: 'var(--text-xs)' }}>
                Saving changes the next job from this button; jobs already queued keep the prompt they were sent with.
                {lostSkill ? ` The agent picks its skill from this text, so keep the line that names the ${skillName} skill.` : ''}
              </span>
              <div style={{ display: 'flex', gap: '8px' }}>
                {draft !== fallback ? (
                  <Button size="sm" variant="ghost" onClick={() => setDraft(fallback)} disabled={busy}>
                    Load the built-in default
                  </Button>
                ) : null}
              </div>
            </div>
          </div>
        ) : versions == null ? (
          <span style={faint}>Loading…</span>
        ) : (
          <TemplateText text={current} />
        )}
        <div style={{ marginTop: 'var(--space-4)' }}>
          <div className="t-eyebrow" style={{ marginBottom: '6px' }}>
            Variables this button fills{editing ? ' · click one to insert it' : ''}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'auto minmax(0, 1fr)', gap: '6px 14px', fontSize: 'var(--text-xs)', alignItems: 'baseline' }}>
            {Object.entries(entry.prompt.vars).map(([name, what]) => (
              <VarRow key={name} name={name} what={what} onInsert={editing ? () => insertVar(name) : undefined} used={!check.unused.includes(name)} />
            ))}
          </div>
          <div style={{ ...faint, fontSize: 'var(--text-xs)', marginTop: '8px' }}>
            A line whose variables all come out empty is left out, so optional lines take care of themselves.
          </div>
        </div>
      </Card>

      {children}

      <Card eyebrow="History" title={versions?.length ? `${versions.length} save${versions.length === 1 ? '' : 's'}` : 'Never edited'}>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {(versions ?? []).map((v, i) => (
            <HistoryRow
              key={v.id}
              label={v.template == null ? 'Back to the built-in default' : v.note || 'Edited the prompt'}
              meta={`${when(v.created_at)}${v.created_by && names.get(v.created_by) ? ` · ${names.get(v.created_by)}` : ''}`}
              current={i === 0}
              open={versionParam === v.id}
              onClick={() => setVersionParam(versionParam === v.id ? null : v.id)}
            />
          ))}
          <HistoryRow
            label="Built-in default"
            meta="ships with Cockpit"
            current={!saved?.template}
            open={versionParam === 'default'}
            onClick={() => setVersionParam(versionParam === 'default' ? null : 'default')}
          />
        </div>
        {viewing ? (
          <div style={{ marginTop: 'var(--space-3)', display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
            <TemplateText text={viewing.template ?? fallback} />
            {(viewing.template ?? fallback) !== current ? (
              <div>
                <Button size="sm" variant="secondary" disabled={busy} onClick={() => void save(viewing.id === 'default' ? null : viewing.template, `Restored the ${viewing.id === 'default' ? 'built-in default' : `version from ${when((viewing as PromptVersion).created_at)}`}`)}>
                  {viewing.id === 'default' ? 'Go back to the built-in default' : 'Use this version again'}
                </Button>
              </div>
            ) : (
              <span style={{ ...faint, fontSize: 'var(--text-xs)' }}>This is what the button sends now.</span>
            )}
          </div>
        ) : null}
      </Card>
    </>
  );
}

function VarRow({ name, what, onInsert, used }: { name: string; what: string; onInsert?: () => void; used: boolean }) {
  const chip = (
    <code
      style={{
        fontFamily: 'var(--font-mono)',
        color: used ? 'var(--accent-text)' : 'var(--text-faint)',
        whiteSpace: 'nowrap',
        textDecoration: used ? 'none' : 'line-through',
      }}
    >
      {`{{${name}}}`}
    </code>
  );
  return (
    <>
      {onInsert ? (
        <button type="button" onClick={onInsert} className="hover-wash" style={{ all: 'unset', cursor: 'pointer', borderRadius: '4px', padding: '0 4px', margin: '0 -4px' }} title="Insert at the cursor">
          {chip}
        </button>
      ) : (
        chip
      )}
      <span style={{ color: 'var(--text-muted)' }}>{what}</span>
    </>
  );
}

function HistoryRow({ label, meta, current, open, onClick }: { label: string; meta: string; current: boolean; open: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="hover-wash"
      style={{
        all: 'unset',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '8px 10px',
        margin: '0 -10px',
        borderRadius: 'var(--radius-sm)',
        background: open ? 'var(--surface-input)' : undefined,
      }}
    >
      <span style={{ flex: 1, minWidth: 0, fontSize: 'var(--text-sm)', color: 'var(--text-body)' }}>{label}</span>
      {current ? <Badge tone="up">In use</Badge> : null}
      <span className="t-num" style={{ ...faint, fontSize: 'var(--text-xs)', whiteSpace: 'nowrap' }}>
        {meta}
      </span>
    </button>
  );
}
